/**
 * @module CommentItemCheckinPreview
 *
 * Component that displays a check-in preview inside CommentItem for comments related to a check-in.
 *
 * @remarks
 * This render-function is a workaround for a circular dependency:
 * - CommentItem → CheckinPreviewItem → CommentItem → ... infinitely
 * - CheckinPreviewItem → CommentItem → CommentItem ... infinitely
 *
 * Normally, this could be resolved using defineAsyncComponent, but async imports
 * currently do not work correctly in the server version of our app due to incorrect $contextPath replacement.
 * Solution of $contextPath replacement is found but not implemented yet.
 *
 * @warning
 * - Do not use this component outside CommentItem. ⚠️
 * - Do not remove it. ⚠️
 * - Do not change its usage until the async import issue is fixed. ⚠️
 *
 * @see CommentItem
 * @see CheckinPreviewItem
 */
import { defineComponent, getCurrentInstance, h } from 'vue'

import { IS_PRODUCTION } from '@root/app-modes'

import CheckinPreviewItem from '@/components/objectives/element-check-in/CheckinPreviewItem'

export default defineComponent({
  name: 'CommentItemCheckinPreview',
  props: {
    checkinData: {
      type: Object,
      required: true
    },

    okrElement: {
      type: Object,
      required: true
    }
  },

  render(props) {
    const instance = getCurrentInstance()

    const correctParentComponentName = 'CommentItem'
    const correctParentComponentFileName = `${correctParentComponentName}.vue`

    const parentType = instance?.parent?.type || {}

    const parentComponentName = parentType.name || 'unknown'
    const parentComponentFilePath = parentType.__file || 'unknown'

    const isParentCorrect =
      parentComponentName === correctParentComponentName &&
      (IS_PRODUCTION || parentComponentFilePath.endsWith(correctParentComponentFileName))

    if (!isParentCorrect) {
      throw new Error(`This component should only be used inside ${correctParentComponentFileName}`)
    }
    const { checkinData, okrElement } = props
    return h(CheckinPreviewItem, { okrElement, item: checkinData, showAuthor: false })
  }
})
